import { useCallback, useEffect, useRef, useState } from 'react';
import { Gamepad2, RotateCcw, Trophy } from 'lucide-react';
import { api } from '../api';

/**
 * 2048 – malá hra na prehľade.
 *
 * Prečo práve táto: nemá časový tlak, dá sa kedykoľvek nechať tak a vrátiť sa,
 * a zmestí sa do jednej dlaždice. Hra na strieľačku by v systéme na objednávky
 * pôsobila divne, hra na čas by nútila človeka dohrať to aj keď príde zákazník.
 *
 * Šípky sa **nepočúvajú globálne** – klávesnicu berie iba dlaždica, keď je na
 * nej kurzor (má `tabIndex`). Inak by hra kradla šípky pri vypĺňaní objednávky.
 */

const N = 4;
const prazdna = () => Array.from({ length: N * N }, () => 0);

/** Náhodné voľné políčko: 2 (deväťkrát z desiatich) alebo 4. */
function pridajDlazdicu(pole) {
  const volne = pole.map((v, i) => (v === 0 ? i : -1)).filter((i) => i >= 0);
  if (!volne.length) return pole;
  const kam = volne[Math.floor(Math.random() * volne.length)];
  const nove = [...pole];
  nove[kam] = Math.random() < 0.9 ? 2 : 4;
  return nove;
}

/** Jeden riadok posunutý doľava + získané body. */
function posunRiadok(riadok) {
  const cisla = riadok.filter(Boolean);
  const out = [];
  let body = 0;
  for (let i = 0; i < cisla.length; i++) {
    if (cisla[i] === cisla[i + 1]) {
      out.push(cisla[i] * 2);
      body += cisla[i] * 2;
      i++;                       // spojenú dvojicu preskočíme, nespája sa reťazovo
    } else out.push(cisla[i]);
  }
  while (out.length < N) out.push(0);
  return { riadok: out, body };
}

/* Ťah v ľubovoľnom smere sa spraví tak, že sa políčka prečítajú v poradí, ktoré
 * daný smer zodpovedá posunu doľava. Netreba tak štyri kusy takmer rovnakého
 * kódu. */
const INDEXY = {
  vlavo: (r, i) => r * N + i,
  vpravo: (r, i) => r * N + (N - 1 - i),
  hore: (r, i) => i * N + r,
  dole: (r, i) => (N - 1 - i) * N + r,
};

function tah(pole, smer) {
  const kam = INDEXY[smer];
  const nove = [...pole];
  let body = 0;
  let zmena = false;
  for (let r = 0; r < N; r++) {
    const vstup = Array.from({ length: N }, (_, i) => pole[kam(r, i)]);
    const v = posunRiadok(vstup);
    body += v.body;
    for (let i = 0; i < N; i++) {
      if (nove[kam(r, i)] !== v.riadok[i]) zmena = true;
      nove[kam(r, i)] = v.riadok[i];
    }
  }
  return { pole: nove, body, zmena };
}

const daSaHrat = (pole) =>
  pole.some((v) => v === 0) || Object.keys(INDEXY).some((s) => tah(pole, s).zmena);

/* Farby podľa hodnoty. Vyššie čísla sú tmavšie, nech je vidieť, ako to rastie. */
const FARBY = {
  0: 'bg-slate-100',
  2: 'bg-slate-200 text-slate-700',
  4: 'bg-amber-100 text-amber-800',
  8: 'bg-amber-200 text-amber-900',
  16: 'bg-orange-300 text-white',
  32: 'bg-orange-400 text-white',
  64: 'bg-orange-500 text-white',
  128: 'bg-brand-400 text-white',
  256: 'bg-brand-500 text-white',
  512: 'bg-brand-600 text-white',
  1024: 'bg-brand-700 text-white',
  2048: 'bg-slate-800 text-white',
};
const farba = (v) => FARBY[v] || 'bg-slate-900 text-white';
const velkost = (v) => (v >= 1024 ? 'text-base' : v >= 128 ? 'text-lg' : 'text-xl');

export default function Hra2048() {
  const [pole, setPole] = useState(() => pridajDlazdicu(pridajDlazdicu(prazdna())));
  const [skore, setSkore] = useState(0);
  const [koniec, setKoniec] = useState(false);
  const [rekord, setRekord] = useState(null);
  const [novyRekord, setNovyRekord] = useState(false);
  const plocha = useRef(null);
  const dotyk = useRef(null);

  useEffect(() => { api.get('/api/stats/hra').then(setRekord).catch(() => {}); }, []);

  /* Skóre sa posiela až na konci hry – nie po každom ťahu. Server by inak dostal
   * desiatky zápisov za jednu partiu. */
  const ulozSkore = useCallback((konecne) => {
    if (konecne <= 0) return;
    api.post('/api/stats/hra', { skore: konecne })
      .then((v) => { setRekord(v); setNovyRekord(!!v.novy); })
      .catch(() => {});
  }, []);

  const hraj = useCallback((smer) => {
    if (koniec) return;
    setPole((stare) => {
      const v = tah(stare, smer);
      if (!v.zmena) return stare;
      const s = pridajDlazdicu(v.pole);
      setSkore((x) => {
        const novy = x + v.body;
        if (!daSaHrat(s)) { setKoniec(true); ulozSkore(novy); }
        return novy;
      });
      return s;
    });
  }, [koniec, ulozSkore]);

  const nova = () => {
    setPole(pridajDlazdicu(pridajDlazdicu(prazdna())));
    setSkore(0);
    setKoniec(false);
    setNovyRekord(false);
    plocha.current?.focus();
  };

  const klaves = (e) => {
    const smer = {
      ArrowLeft: 'vlavo', ArrowRight: 'vpravo', ArrowUp: 'hore', ArrowDown: 'dole',
      a: 'vlavo', d: 'vpravo', w: 'hore', s: 'dole',
    }[e.key];
    if (!smer) return;
    e.preventDefault();      // aby sa pod hrou nerolovala stránka
    hraj(smer);
  };

  /* Na mobile sa hrá ťahom prsta. */
  const dotykStart = (e) => {
    const t = e.touches[0];
    dotyk.current = { x: t.clientX, y: t.clientY };
  };
  const dotykKoniec = (e) => {
    if (!dotyk.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - dotyk.current.x;
    const dy = t.clientY - dotyk.current.y;
    dotyk.current = null;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    hraj(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'vpravo' : 'vlavo') : (dy > 0 ? 'dole' : 'hore'));
  };

  return (
    <div className="card-p">
      <div className="mb-3 flex items-center gap-2">
        <Gamepad2 size={16} className="text-slate-400" />
        <h2 className="font-semibold text-slate-700">Chvíľka oddychu</h2>
        <span className="text-xs text-slate-400">2048</span>
        <button className="ml-auto text-xs text-slate-400 hover:text-slate-600" onClick={nova}>
          <RotateCcw size={13} className="mr-1 inline" />Nová hra
        </button>
      </div>

      <div className="mb-2 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Skóre</div>
          <div className="text-2xl font-semibold leading-tight text-slate-800">{skore}</div>
        </div>
        {rekord?.skore > 0 && (
          <div className="text-right">
            <div className="flex items-center justify-end gap-1 text-[11px] uppercase tracking-wide text-slate-400">
              <Trophy size={11} /> Rekord
            </div>
            <div className="text-sm font-medium text-slate-600">{rekord.skore}</div>
            {rekord.meno && <div className="text-[11px] text-slate-400">{rekord.meno}</div>}
          </div>
        )}
      </div>

      <div
        ref={plocha}
        tabIndex={0}
        onKeyDown={klaves}
        onTouchStart={dotykStart}
        onTouchEnd={dotykKoniec}
        className="relative mx-auto w-full max-w-[15rem] rounded-xl bg-slate-200/70 p-2 outline-none ring-brand-500/40 focus:ring-2"
      >
        <div className="grid grid-cols-4 gap-2">
          {pole.map((v, i) => (
            <div key={i}
              className={`flex aspect-square items-center justify-center rounded-lg font-bold tabular-nums transition-colors ${farba(v)} ${velkost(v)}`}>
              {v || ''}
            </div>
          ))}
        </div>

        {koniec && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-white/85">
            <div className="text-sm font-semibold text-slate-700">
              {novyRekord ? 'Nový rekord!' : 'Koniec hry'}
            </div>
            <div className="mb-2 text-xs text-slate-500">skóre {skore}</div>
            <button className="btn bg-brand-600 text-white hover:bg-brand-500" onClick={nova}>
              Hrať znova
            </button>
          </div>
        )}
      </div>

      <p className="mt-2 text-center text-[11px] text-slate-400">
        Kliknite do hry a hrajte šípkami. Na mobile ťahom prsta.
      </p>
    </div>
  );
}
